import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'

export type CenterPanelLayout = 'default' | 'wide'

interface CenterPanelLayoutContextValue {
  layout: CenterPanelLayout
  setLayout: (mode: CenterPanelLayout) => void
}

const CenterPanelLayoutContext = createContext<CenterPanelLayoutContextValue | null>(null)

/**
 * Provides `CenterPanelLayout` state to the center panel subtree.
 * Mounted once by `CenterPanel`; child routes update the layout via `useCenterPanelLayout`.
 */
export const CenterPanelLayoutProvider = ({ children }: { children: ReactNode }) => {
  const [layout, setLayout] = useState<CenterPanelLayout>('default')
  const value = useMemo(() => ({ layout, setLayout }), [layout])

  return (
    <CenterPanelLayoutContext.Provider value={value}>
      {children}
    </CenterPanelLayoutContext.Provider>
  )
}

/** Internal — consumed by CenterPanelContent only. */
export const useCenterPanelLayoutContext = (): CenterPanelLayoutContextValue => {
  const ctx = useContext(CenterPanelLayoutContext)
  if (!ctx) throw new Error('useCenterPanelLayoutContext must be used within CenterPanelLayoutProvider')
  return ctx
}

/**
 * Sets the center panel layout mode for the current route (new app shell only).
 * No-ops when `enabled` is false or when there is no provider (legacy layout).
 * Resets to 'default' on unmount — prevents mode bleed between routes.
 */
export const useCenterPanelLayout = (mode: CenterPanelLayout, enabled = true): void => {
  const ctx = useContext(CenterPanelLayoutContext)
  const setLayout = ctx?.setLayout

  useEffect(() => {
    if (!enabled || !setLayout) return undefined
    setLayout(mode)
    return () => setLayout('default')
  }, [enabled, mode, setLayout])
}

/** Current center panel layout, or `null` outside the new app shell (no provider). */
export const useCenterPanelLayoutMode = (): CenterPanelLayout | null => {
  const ctx = useContext(CenterPanelLayoutContext)
  return ctx?.layout ?? null
}
